import { useState, useCallback, useRef, useEffect } from 'react'
import {
  generateShots,
  groupShotsToScenes
} from '../../../services/api/aiVideoGeneration'
import { useTaskPoller } from './useTaskPoller'
import { normalizeSceneAudioHistory } from '../utils/audioHistory'

const resolveId = value => {
  if (!value) return null
  if (typeof value === 'string') return value
  if (typeof value === 'number') return String(value)
  if (typeof value === 'object') {
    return (
      value?.id || value?.sessionId || value?.session_id || value?._id || null
    )
  }
  return null
}

const getCreativeGuidelineSessionId = (source, videoId) => {
  const fromSource =
    resolveId(source?.sessionId) ||
    resolveId(source?.session_id) ||
    resolveId(source?.creative_guideline_session_id) ||
    resolveId(source?.creativeGuidelineSessionId) ||
    resolveId(source?.creativeDirection?.sessionId) ||
    resolveId(source?.creativeDirection?.session_id) ||
    resolveId(source?.creativeDirection?.creative_guideline_session_id) ||
    resolveId(source?.creativeDirection?.creativeGuidelineSessionId) ||
    null

  if (fromSource) return fromSource

  if (typeof window === 'undefined' || !window.localStorage || !videoId) {
    return null
  }

  try {
    return (
      window.localStorage.getItem(`creative-direction-session:${videoId}`) ||
      null
    )
  } catch {
    return null
  }
}

const shouldSendCreativeGuidelineSession = (formData, videoId) =>
  Boolean(getCreativeGuidelineSessionId(formData, videoId))

const getVideoCreationMode = formData =>
  formData?.aiVideoFlowMode ||
  formData?.video_creation_mode ||
  formData?.videoCreationMode ||
  formData?.creativeDirection?.flowMode ||
  formData?.creativeDirection?.flow_mode ||
  null

const parseMaybeJSON = (value, parseJSONSafely) => {
  if (value === undefined || value === null || value === '') return null
  if (parseJSONSafely) return parseJSONSafely(value)
  return value
}

const isObjectValue = value =>
  value && typeof value === 'object' && !Array.isArray(value)

const hasObjectKeys = value =>
  isObjectValue(value) && Object.keys(value).length > 0

const getByKeyVariants = (source = {}, key) => {
  if (!isObjectValue(source)) return undefined

  const normalized = String(key || '').replace(/^__|__$/g, '')
  const variants = [key, normalized, `__${normalized}__`]

  return variants.find(variant =>
    Object.prototype.hasOwnProperty.call(source, variant)
  )
}

const buildOutfitsReference = ({ wardrobe, outfitIds, existing }) => {
  if (hasObjectKeys(existing)) return existing
  if (!isObjectValue(wardrobe)) return existing || null

  const ids = isObjectValue(outfitIds) ? outfitIds : {}

  return Object.entries(wardrobe).reduce((acc, [characterKey, outfitKeys]) => {
    const outfits = Array.isArray(outfitKeys) ? outfitKeys : []

    acc[characterKey] = outfits.reduce((outfitAcc, outfitKey) => {
      const idKey = getByKeyVariants(ids, outfitKey)
      outfitAcc[outfitKey] = idKey ? ids[idKey] : outfitKey
      return outfitAcc
    }, {})

    return acc
  }, {})
}

const buildSelectedCharacters = ({
  characterMap,
  characterIds,
  referenceTags
}) => {
  if (!isObjectValue(characterMap)) return null

  const ids = isObjectValue(characterIds) ? characterIds : {}
  const tags = isObjectValue(referenceTags) ? referenceTags : {}

  return Object.entries(characterMap).map(([characterKey, characterValue]) => {
    const idKey = getByKeyVariants(ids, characterKey)
    const tagKey = getByKeyVariants(tags, characterKey)
    const characterName = isObjectValue(characterValue)
      ? characterValue.name || characterValue.character_name || characterKey
      : characterValue || characterKey
    const mappedCharacterId = isObjectValue(characterValue)
      ? characterValue.character_id || characterValue.id || characterValue.tag
      : null
    const normalizedName = String(characterName || characterKey).replace(
      /^__|__$/g,
      ''
    )

    return {
      original: characterKey,
      last: characterKey,
      current: `__${normalizedName}__`,
      tag: idKey
        ? ids[idKey]
        : tagKey
        ? tags[tagKey]
        : mappedCharacterId || characterKey
    }
  })
}

const buildCreativeGuidelineHydration = (
  shotsData = {},
  parseJSONSafely,
  { hydrateGeneratedSelections = false } = {}
) => {
  const sourceData = shotsData.output || shotsData
  const locationMap = parseMaybeJSON(sourceData.location_map, parseJSONSafely)
  const characterMap = parseMaybeJSON(sourceData.character_map, parseJSONSafely)
  const outfitMap = parseMaybeJSON(sourceData.outfit_map, parseJSONSafely)
  const propMap = parseMaybeJSON(sourceData.prop_map, parseJSONSafely)
  const props = parseMaybeJSON(sourceData.props, parseJSONSafely)
  const wardrobe = parseMaybeJSON(sourceData.wardrobe, parseJSONSafely)
  const outfitIds = parseMaybeJSON(sourceData.outfit_ids, parseJSONSafely)
  const referenceTags = parseMaybeJSON(
    sourceData.reference_tags,
    parseJSONSafely
  )
  const characterIds = parseMaybeJSON(sourceData.character_ids, parseJSONSafely)
  const locationReference = parseMaybeJSON(
    sourceData.location_reference || sourceData.locationReference,
    parseJSONSafely
  )
  const propsReference = parseMaybeJSON(
    sourceData.props_reference ||
      sourceData.prop_reference ||
      sourceData.propsReference,
    parseJSONSafely
  )
  const outfitsReference = hydrateGeneratedSelections
    ? buildOutfitsReference({
        wardrobe,
        outfitIds,
        existing: parseMaybeJSON(
          sourceData.outfits_reference || sourceData.outfitsReference,
          parseJSONSafely
        )
      })
    : {}

  return {
    locationMap,
    characterMap,
    selectedCharacter: hydrateGeneratedSelections
      ? buildSelectedCharacters({
          characterMap,
          characterIds,
          referenceTags
        })
      : [],
    outfitMap,
    propMap,
    props,
    wardrobe,
    locationReference,
    props_reference: propsReference,
    outfits_reference: outfitsReference,
    referenceTags,
    characterIds,
    outfitIds
  }
}

const getDefinedHydrationValues = hydration =>
  Object.entries(hydration || {}).reduce((acc, [key, value]) => {
    if (value === undefined || value === null) return acc
    if (isObjectValue(value) && !Object.keys(value).length) return acc
    acc[key] = value
    return acc
  }, {})

/**
 * Custom hook to manage shot generation and scene mapping
 * Handles fetching shots, polling status, and formatting scene data
 */
export const useShotsGeneration = ({
  formData,
  setFormData,
  onLocationMapGenerate,
  onAudioClustering,
  parseJSONSafely,
  videoId
} = {}) => {
  const isFetchingShots = useRef(false)
  const [shotsGenerationProgress, setShotsGenerationProgress] = useState(0)
  const [isClustering, setIsClustering] = useState(false)
  const [clusteringProgress, setClusteringProgress] = useState(0)
  const shotsPoller = useTaskPoller({
    pollInterval: 2000,
    continuePollingOnError: false
  })
  const clusteringPoller = useTaskPoller({
    pollInterval: 15000,
    continuePollingOnError: false
  })

  /**
   * Format scene object from API response
   */
  const formatSceneObj = useCallback(sceneObj => {
    return Object.entries(sceneObj)
      .map(([sceneKey, sceneValue]) => {
        const { scene_location = '', shots = [] } = sceneValue

        // Normalize shots to a consistent array of { key, value }
        let shotsToProcess = []
        if (Array.isArray(shots)) {
          shotsToProcess = shots.map(s => {
            const k = Object.keys(s)[0]
            return { key: k, value: s[k] }
          })
        } else if (typeof shots === 'object' && shots !== null) {
          shotsToProcess = Object.entries(shots).map(([k, v]) => ({
            key: k,
            value: v
          }))
        }

        const formattedShots = shotsToProcess.map(
          ({ key: shotKey, value: shotValue }, index) => {
            return {
              ...shotValue,
              id: `${sceneKey}-shot-${index + 1}`,
              key: shotKey,
              title: shotValue.title || '',
              initial_image_prompt:
                shotValue['initial image prompt'] ||
                shotValue.initial_image_prompt ||
                '',
              scenery: shotValue.scenery || '',
              shot_direction:
                shotValue['shot direction'] || shotValue.shot_direction || '',
              video_prompt:
                shotValue['video prompt'] || shotValue.video_prompt || '',
              voiceover_text:
                shotValue['voiceover text'] || shotValue.voiceover_text || '',
              lighting: shotValue.lighting || '',
              dialogue: shotValue.dialogue || {},
              characters: shotValue.characters || [],
              video_duration: shotValue.video_duration || '',
              vidSrc: shotValue.video_url || shotValue.vidSrc || ''
            }
          }
        )

        return {
          id: sceneKey,
          title: sceneKey,
          description: scene_location,
          shots: formattedShots,
          aggregated_voiceover: sceneValue.aggregated_voiceover || '',
          audio_duration: sceneValue.audio_duration || 0,
          audio_duration_estimated:
            sceneValue.audio_duration_estimated || false,
          estimated_audio_duration: sceneValue.estimated_audio_duration || null,
          audio_link: sceneValue.audio_link || '',
          audio_history: sceneValue.audio_history || [],
          scene_location: scene_location,
          shot_duration_map: sceneValue.shot_duration_map || null
        }
      })
      .map(normalizeSceneAudioHistory)
  }, [])

  const updateFormDataWithShots = useCallback(
    (sceneData, shotsData) => {
      const formattedSceneData = formatSceneObj(sceneData)
      const isAdvancedFlow = getVideoCreationMode(formData) === 'advanced'
      const creativeGuidelineHydration = getDefinedHydrationValues(
        buildCreativeGuidelineHydration(shotsData, parseJSONSafely, {
          hydrateGeneratedSelections: isAdvancedFlow
        })
      )
      const modeAwareHydration = isAdvancedFlow
        ? creativeGuidelineHydration
        : {
            ...creativeGuidelineHydration,
            selectedCharacter: [],
            outfits_reference: {}
          }

      setFormData(prev => ({
        ...prev,
        shotList: formattedSceneData,
        shotsApiRes: shotsData,
        ...modeAwareHydration
      }))

      return formattedSceneData
    },
    [
      formatSceneObj,
      formData?.aiVideoFlowMode,
      formData?.video_creation_mode,
      formData?.videoCreationMode,
      formData?.creativeDirection?.flowMode,
      formData?.creativeDirection?.flow_mode,
      parseJSONSafely,
      setFormData
    ]
  )

  /**
   * Handle scene mapping from shot data
   */
  const handleScenesMapping = useCallback(
    async (data, isFineTuned) => {
      try {
        const sourceShotsData = data && data.output ? data.output : data
        const payload = {
          input_data: sourceShotsData,
          video_id: videoId
        }

        setIsClustering(true)
        setClusteringProgress(0)

        const response = await groupShotsToScenes(payload)

        if (response.data && response.data.task_id) {
          const result = await clusteringPoller.startPolling(
            response.data.task_id
          )

          if (result && result.status === 'success' && result.output) {
            const formattedSceneData = updateFormDataWithShots(
              result.output,
              data
            )
            setClusteringProgress(100)

            if (onAudioClustering && formData?.selectedVoice) {
              await onAudioClustering(result.output, sourceShotsData)
            }

            return formattedSceneData
          }
        } else {
          const sceneData = response.data && response.data.output
          const formattedSceneData = updateFormDataWithShots(sceneData, data)

          console.log('from shots', data)

          if (onAudioClustering && formData?.selectedVoice) {
            await onAudioClustering(sceneData, sourceShotsData)
          }
        }
      } catch (error) {
        console.error('Scene mapping failed:', error)
      } finally {
        setFormData(prev => ({ ...prev, shotLoading: false }))
        setIsClustering(false)
        setClusteringProgress(0)
      }
    },
    [
      clusteringPoller,
      formData?.selectedVoice,
      onAudioClustering,
      setFormData,
      updateFormDataWithShots,
      videoId
    ]
  )

  /**
   * Poll shots task status
   */
  const pollShotsTaskStatus = useCallback(
    async taskId => {
      if (!isFetchingShots.current) return

      const result = await shotsPoller.startPolling(taskId)

      if (result && result.status === 'success' && result.output) {
        setShotsGenerationProgress(100)
        await handleScenesMapping(result.output)
      }
    },
    [handleScenesMapping, shotsPoller]
  )

  /**
   * Fetch shots from voiceover text
   */
  const fetchShots = useCallback(
    async (retryStyleId = null) => {
      const styleIdArg = typeof retryStyleId === 'string' ? retryStyleId : null

      if (isFetchingShots.current || shotsPoller.isPolling) return
      isFetchingShots.current = true

      setFormData(prev => ({
        ...prev,
        shotLoading: true,
        shotGenerationError: null
      }))
      const storyText = formData && formData.voiceoverText
      // Shot generation must use the selected shot style id (not video style key).
      let styleId =
        styleIdArg ||
        formData?.shotGenerationStyle?.id ||
        formData?.shotGenerationStyle?.raw?.id ||
        formData?.shotGenerationStyle?.key ||
        (typeof formData?.shotGenerationStyle === 'string'
          ? formData.shotGenerationStyle
          : null) ||
        null

      if (styleId === 'default_style') styleId = null

      if (!storyText) {
        console.error('fetchShots called with empty storyText')
        setFormData(prev => ({
          ...prev,
          shotLoading: false,
          shotGenerationError: 'Story text is required before generating shots.'
        }))
        isFetchingShots.current = false
        return
      }

      try {
        const creativeGuidelineSessionId = getCreativeGuidelineSessionId(
          formData,
          videoId
        )
        const shouldUseCreativeGuideline = shouldSendCreativeGuidelineSession(
          formData,
          videoId
        )

        const res = await generateShots({
          story: storyText,
          style_id: styleId,
          video_id: videoId,
          video_mode: formData.audioMode,
          language: formData?.language || 'en',
          ...(shouldUseCreativeGuideline
            ? { creative_guideline_session: creativeGuidelineSessionId }
            : {}),
          video_artistic_style:
            formData?.videoArtisticStyle === null
              ? 'realistic'
              : formData?.videoArtisticStyle
        })
        const taskId = res && res.data && res.data.task_id

        if (taskId) {
          await pollShotsTaskStatus(taskId)
        } else {
          const shotsData = res && res.data
          await handleScenesMapping(shotsData)
        }
      } catch (err) {
        console.error('Error generating shots:', err)
        const errorMessage =
          err?.response?.data?.error_msg ||
          err?.response?.data?.message ||
          err?.message ||
          'Shot generation failed.'

        setFormData(prev => ({
          ...prev,
          shotLoading: false,
          shotGenerationError: errorMessage
        }))
      } finally {
        isFetchingShots.current = false
      }
    },
    [
      formData?.voiceoverText,
      formData?.language,
      formData?.audioMode,
      formData?.creativeDirection,
      formData?.shotGenerationStyle,
      formData?.videoArtisticStyle,
      setFormData,
      pollShotsTaskStatus,
      handleScenesMapping,
      shotsPoller.isPolling,
      videoId
    ]
  )

  /**
   * Reset shots generation state
   */
  const resetShotsGeneration = useCallback(() => {
    isFetchingShots.current = false
    shotsPoller.stopPolling()
    clusteringPoller.stopPolling()
    setShotsGenerationProgress(0)
    setIsClustering(false)
    setClusteringProgress(0)
  }, [clusteringPoller, shotsPoller])

  useEffect(() => {
    setShotsGenerationProgress(shotsPoller.progress || 0)
  }, [shotsPoller.progress])

  useEffect(() => {
    setIsClustering(clusteringPoller.isPolling)
  }, [clusteringPoller.isPolling])

  useEffect(() => {
    setClusteringProgress(clusteringPoller.progress || 0)
  }, [clusteringPoller.progress])

  return {
    // State
    isFetchingShots: isFetchingShots.current,
    shotsGenerationProgress,
    isClustering,
    clusteringProgress,

    // Actions
    fetchShots,
    handleScenesMapping,
    resetShotsGeneration,

    // Utilities
    formatSceneObj
  }
}

export default useShotsGeneration
